import type { OsmAdminBoundary, OsmAdminLevel } from '@roadworld/shared';

/** Player-position → containing admin region(s).
 *
 *  Boundaries arrive piecemeal from chunks. The same OSM relation can appear in many
 *  chunks (a city polygon spans many 1.5 km chunks), so we dedupe by relation id.
 *  Lookup walks all loaded polygons and runs ray-casting point-in-polygon. With at most a
 *  few dozen admin polygons loaded in a session, this is well under a millisecond. */
export class AreaIndex {
  private byId = new Map<number, OsmAdminBoundary>();

  addBoundaries(boundaries: readonly OsmAdminBoundary[]): void {
    for (const b of boundaries) {
      if (!this.byId.has(b.id)) this.byId.set(b.id, b);
    }
  }

  clear(): void {
    this.byId.clear();
  }

  /** Returns the smallest-numbered (== highest-admin-level == most specific) boundary the
   *  point falls inside, per level. Map keys are 6/8/9/10. */
  regionsAt(lat: number, lng: number): Map<OsmAdminLevel, OsmAdminBoundary> {
    const out = new Map<OsmAdminLevel, OsmAdminBoundary>();
    for (const b of this.byId.values()) {
      if (out.has(b.adminLevel)) continue;
      if (pointInMultiPolygon(lng, lat, b.rings)) {
        out.set(b.adminLevel, b);
      }
    }
    return out;
  }

  size(): number {
    return this.byId.size;
  }
}

/** Returns true if (x, y) is inside any of the rings. Rings are [x, y] = [lng, lat] order.
 *  Even–odd rule: inside iff an odd number of rings contain the point. (For admin
 *  multipolygons, components are disjoint, so this collapses to "any ring contains".) */
function pointInMultiPolygon(x: number, y: number, rings: ReadonlyArray<ReadonlyArray<readonly [number, number]>>): boolean {
  let inside = false;
  for (const ring of rings) {
    if (pointInRing(x, y, ring)) inside = !inside;
  }
  return inside;
}

function pointInRing(x: number, y: number, ring: ReadonlyArray<readonly [number, number]>): boolean {
  let inside = false;
  const n = ring.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const a = ring[i]!;
    const b = ring[j]!;
    const xi = a[0];
    const yi = a[1];
    const xj = b[0];
    const yj = b[1];
    const intersect =
      yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi + 1e-12) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}
